import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { api, errorMessage } from '../../api';
import { StatusPill } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { mapServerError } from '../lib/draft.js';
import { STATUS_LABEL, STATUS_TONE, priceLabel, qtyLabel } from '../lib/list.js';
import { buildVariantInput, emptyVariant, validateVariant, variantsOf, type MartAdminDetail, type VariantDraft } from '../lib/mart.js';
import { photoForm, prepareProductPhoto } from '../photo';
import { ProductArt } from '../art';
import { useInvalidateProduct } from '../hooks';
import { PhotoBadge } from '../ReviewBadge';
import { PHOTO_NOTE } from '../lib/photo-rule.js';

/**
 * Variants of a Mart base product ("Coke 290 ml / 1 L / 1.5 L"): the list, and "Add a variant",
 * which creates a sibling product (its own label, price, barcode, stock and photo).
 */
export function VariantsPanel({ shopId, product, editPathFor }: { shopId: string; product: MartAdminDetail; editPathFor(id: string): string }) {
  const variants = variantsOf(product);
  const [adding, setAdding] = useState(false);
  return (
    <section className="card panel" aria-labelledby="sec-variants">
      <h2 id="sec-variants">Variants</h2>
      <p className="t-caption muted">Sizes or flavours of {product.name}, each with its own price, stock, barcode and photo. Customers pick one on this product.</p>
      {variants.length ? (
        <ul className="stack s8" aria-label={`Variants of ${product.name}`}>
          {variants.map((v) => (
            <li key={v.id} className="cat-combo">
              <ProductArt className="cat-thumb" name={v.name} line={v.line} category={v.categoryName} />
              <span className="grow stack s4">
                <Link className="cat-name" to={editPathFor(v.id)}>
                  {v.variantLabel || v.name}
                </Link>
                <span className="t-caption muted">
                  {priceLabel(v)} · {qtyLabel(v.available, v.stockUnit)}
                </span>
              </span>
              {v.status !== 'active' ? <StatusPill tone={STATUS_TONE[v.status]}>{STATUS_LABEL[v.status]}</StatusPill> : null}
              <PhotoBadge product={v} />
            </li>
          ))}
        </ul>
      ) : null}
      {adding ? (
        <AddVariantForm shopId={shopId} product={product} onDone={() => setAdding(false)} />
      ) : (
        <button type="button" className="btn cat-add" onClick={() => setAdding(true)}>
          <Icon name="plus" size="sm" />
          Add a variant
        </button>
      )}
    </section>
  );
}

function AddVariantForm({ shopId, product, onDone }: { shopId: string; product: MartAdminDetail; onDone(): void }) {
  const id = useId();
  const toast = useToast();
  const invalidate = useInvalidateProduct();
  const [v, setV] = useState<VariantDraft>(emptyVariant);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const taken = [product.variantLabel, ...variantsOf(product).map((x) => x.variantLabel)];
  const set = (k: keyof VariantDraft, val: string) => {
    setV((x) => ({ ...x, [k]: val }));
    setErrors((e) => (e[k] || e.form ? (({ [k]: _a, form: _b, ...rest }) => rest)(e) : e));
  };

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.createProduct(shopId, buildVariantInput(product, v));
      let photoError: string | null = null;
      if (photo) {
        try {
          await api.uploadProductImage(shopId, created.id, photoForm(await prepareProductPhoto(photo)));
        } catch (err) {
          photoError = err instanceof Error && !(err as { status?: number }).status ? err.message : errorMessage(err, 'The photo didn’t upload.');
        }
      }
      return { created, photoError };
    },
    onSuccess: ({ created, photoError }) => {
      invalidate(shopId);
      const label = v.label.trim();
      toast(
        photoError
          ? `${label} is added, but the photo didn’t upload: ${photoError} Add it on the variant.`
          : created.needsPhoto && !photo
            ? `${product.name} ${label} is added. Add its photo on the variant to put it on sale.`
            : `${product.name} ${label} is added.`,
        photoError ? 'error' : 'info',
      );
      onDone();
    },
    onError: (err) => {
      const m = mapServerError(err as { status?: number; field?: string; message?: string });
      const field = m?.field === 'variantLabel' ? 'label' : m?.field === 'price' ? 'price' : m?.field === 'barcode' ? 'barcode' : m?.field === 'initialStock' ? 'stock' : 'form';
      setErrors({ [field]: m?.message ?? errorMessage(err, 'Couldn’t add it. Try again.') });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const errs = validateVariant(v, { takenLabels: taken });
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      document.getElementById(`${id}-${first}`)?.focus();
      return;
    }
    create.mutate();
  };

  const field = (k: keyof VariantDraft, label: string, extra: { mono?: boolean; numeric?: boolean; help?: string; prefix?: string } = {}) => {
    const err = errors[k];
    const input = (
      <input
        id={`${id}-${k}`}
        className={extra.prefix ? undefined : `input${extra.mono ? ' t-mono' : ''}`}
        inputMode={extra.numeric ? 'numeric' : k === 'price' ? 'decimal' : undefined}
        autoComplete="off"
        value={v[k]}
        aria-invalid={err ? true : undefined}
        aria-describedby={err ? `${id}-${k}-err` : undefined}
        onChange={(e) => set(k, e.target.value)}
      />
    );
    return (
      <div className="field">
        <label htmlFor={`${id}-${k}`}>{label}</label>
        {extra.prefix ? (
          <div className="input-affix" data-invalid={err ? 'true' : undefined}>
            <span aria-hidden="true">{extra.prefix}</span>
            {input}
          </div>
        ) : (
          input
        )}
        {extra.help && !err ? <span className="help">{extra.help}</span> : null}
        {err ? (
          <span id={`${id}-${k}-err`} className="pill st-error err-pill cat-wrap">
            {err}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <form className="stack s12 cat-fieldset" onSubmit={submit} noValidate aria-label={`Add a variant of ${product.name}`}>
      {field('label', 'Label', { help: 'Like 1 L, 1.5 L or Sweet.' })}
      {field('price', 'Price', { prefix: '₱' })}
      {field('barcode', 'Barcode (optional)', { mono: true, numeric: true })}
      {field('stock', 'How many you have now', { numeric: true, help: 'Leave empty for none yet.' })}
      <div className="field">
        <label htmlFor={`${id}-photo`}>Photo (required to sell it)</label>
        {preview ? <img className="cat-thumb" src={preview} alt="The variant’s photo" /> : null}
        <input
          id={`${id}-photo`}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            e.target.value = '';
            if (f && /^image\//.test(f.type)) setPhoto(f);
          }}
        />
        <span className="help">{photo ? PHOTO_NOTE : `It goes on sale once it has a photo. ${PHOTO_NOTE}`}</span>
      </div>
      {errors.form ? (
        <span className="pill st-error err-pill cat-wrap" role="alert">
          {errors.form}
        </span>
      ) : null}
      <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-quiet" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn" disabled={create.isPending} aria-busy={create.isPending || undefined}>
          {create.isPending ? 'Adding…' : 'Add variant'}
        </button>
      </div>
    </form>
  );
}
